/**
 * Generic image option selector for mini-modal parameter pickers.
 * Supports both radio (single value) and checkbox (multi values) behavior.
 */

import Tooltip from "@mui/material/Tooltip";
import React, { type CSSProperties, type ReactElement, useCallback, useMemo } from "react";
import { getUIColorPalette } from "../../theme/ColorPalette";

/** Renders tooltip copy as HTML (caller-controlled strings from app code). */
const renderTooltipTitle = ( value: string ): ReactElement => (
    <span dangerouslySetInnerHTML={{ "__html": value }} />
);

export interface ImageSelectorOption {

    /** Stable option identifier used for value comparison. */
    "id": string;

    /** Human readable option label displayed below the image. */
    "label": string;

    /** Image source URL/path for the option preview. */
    "imageSrc": string;

    /** Controls image fit mode: true => object-cover, false => object-contain. */
    "imgCover"?: boolean;

    /** Disables interactions for this option when true. */
    "disabled"?: boolean;

    /** Tooltip when disabled; string is parsed as HTML. */
    "disabledTooltip"?: string;

    /** Tooltip when enabled; string is parsed as HTML. */
    "tooltip"?: string;

    /** Optional badge label rendered in the top-right corner. */
    "badgeLabel"?: string;

    /** Optional badge background color. */
    "badgeColor"?: string;
}

interface BaseImageOptionSelectorProps {

    /** Available options to render. */
    "options": readonly ImageSelectorOption[];

    /** Enables dark palette variants for selector visuals. */
    "isDarkUi": boolean;

    /** Number of columns used by the option grid. */
    "columns"?: number;

    /** Optional class names for the root grid wrapper. */
    "className"?: string;
}

interface RadioImageOptionSelectorProps extends BaseImageOptionSelectorProps {

    /** Single selection behavior. */
    "selectionType": "radio";

    /** Current selected option identifier. */
    "value": string;

    /** Emits selected option identifier. */
    "onChange": ( nextValue: string ) => void;
}

interface CheckboxImageOptionSelectorProps extends BaseImageOptionSelectorProps {

    /** Multi selection behavior. */
    "selectionType": "checkbox";

    /** Current selected option identifiers. */
    "value": readonly string[];

    /** Emits updated selected identifiers array. */
    "onChange": ( nextValue: readonly string[] ) => void;
}

type ImageOptionSelectorProps = RadioImageOptionSelectorProps | CheckboxImageOptionSelectorProps;

/**
 * Renders a grid of image-based options with radio or checkbox interactions.
 * @param props Selector behavior, data, and style configuration.
 * @returns Image option selection grid.
 */
const ImageOptionSelector: React.FC<ImageOptionSelectorProps> = ( props ) => {
    const {
        selectionType,
        options,
        isDarkUi,
        columns = 3,
        className = "",
    } = props;
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    /**
     * Resolves selection state for both selector modes:
     * - radio: strict equality on single value
     * - checkbox: membership in the selected values array
     */
    const isOptionSelected = useCallback( ( optionId: string ): boolean => {
        if ( selectionType === "radio" ) {
            return props.value === optionId;
        }

        return props.value.includes( optionId );
    }, [ props.value, selectionType ] );

    /**
     * Normalizes click behavior across radio and checkbox variants.
     * Disabled options are ignored at this layer to keep parent handlers simple.
     */
    const handleOptionClick = useCallback( ( optionId: string, isDisabled: boolean ): void => {
        if ( isDisabled ) {
            return;
        }

        if ( selectionType === "radio" ) {
            props.onChange( optionId );

            return;
        }

        const currentValue = props.value;
        if ( currentValue.includes( optionId ) ) {
            props.onChange( currentValue.filter( ( currentOptionId ) => currentOptionId !== optionId ) );

            return;
        }

        props.onChange( [ ...currentValue, optionId ] );
    }, [ props, selectionType ] );

    const gridStyle: CSSProperties = useMemo( () => {
        // Keep a stable responsive grid while allowing caller-defined density.
        return { "gridTemplateColumns": `repeat(${ columns }, minmax(0, 1fr))` };
    }, [ columns ] );

    return (
        <div
            className={`grid gap-2 ${ className }`}
            style={gridStyle}
        >
            {options.map( ( option ) => {
                const isSelected = isOptionSelected( option.id );
                const isDisabled = option.disabled === true;
                const tooltipTitle = isDisabled === true ? option.disabledTooltip : option.tooltip;
                const showTooltip =
                    tooltipTitle !== undefined && tooltipTitle.length > 0;
                const buttonStyle: CSSProperties = {
                    "borderColor":     isSelected ? palette.overlayBorderStrong : palette.overlayBorder,
                    "backgroundColor": isSelected
                        ? palette.selectorActiveBackgroundStrong
                        : palette.selectorIdleBackground,
                    "opacity": isDisabled ? 0.65 : 1,
                };

                return (
                    <Tooltip
                        arrow
                        disableFocusListener={showTooltip === false}
                        disableHoverListener={showTooltip === false}
                        disableTouchListener={showTooltip === false}
                        key={option.id}
                        placement="top"
                        slotProps={{ "popper": { "sx": { "zIndex": 2147483647 } } }}
                        title={showTooltip === true ? renderTooltipTitle( tooltipTitle ) : undefined}
                    >
                        <button
                            aria-disabled={isDisabled}
                            className={
                                "relative flex flex-col items-center rounded-lg overflow-hidden border " +
                                `transition-all duration-150 hover:opacity-90 ${ isDisabled ? "cursor-not-allowed" : "cursor-pointer" }`
                            }
                            onClick={() => {
                                handleOptionClick( option.id, isDisabled );
                            }}
                            style={buttonStyle}
                            type="button"
                        >
                            {option.badgeLabel !== undefined && option.badgeLabel.length > 0 && (
                                <span
                                    className="absolute right-1 top-1 z-10 inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                                    style={{ "backgroundColor": option.badgeColor ?? "rgb(191, 0, 0)" }}
                                >
                                    {option.badgeLabel}
                                </span>
                            )}
                            <img
                                alt={option.label}
                                className={`block w-full max-h-16 ${ option.imgCover ?? true ? "object-cover" : "object-contain" }`}
                                loading="lazy"
                                src={option.imageSrc}
                            />
                            <span
                                className="text-xs font-medium py-1 w-full text-center px-1"
                                style={{
                                    "color": isSelected
                                        ? palette.selectorActiveText
                                        : palette.selectorIdleText,
                                }}
                            >
                                {option.label}
                            </span>
                        </button>
                    </Tooltip>
                );
            } )}
        </div>
    );
};

export default ImageOptionSelector;
